import type { ReactNode } from "react";
import { BASE_PATH } from "src/basepath";
import { AI_TOOLBOX_SECTION_VALUE } from "src/components/App/components/AppToolboxSlot/AppToolboxSlot.constants";
import { assignToolboxSection } from "src/components/App/components/AppToolboxSlot/AppToolboxSlot.utils";
import { isKnownAppRoutePathname } from "src/utils/appRoutes/appRoutes";

export type InternalAppLink = {
  to: string;
  displayPath: string;
};

const getDefaultUrlBase = () =>
  typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;

const stripBasePath = (pathname: string) => {
  const basePath = BASE_PATH.replace(/\/+$/, "");
  const hasBasePath =
    basePath !== "" &&
    basePath !== "/" &&
    (pathname === basePath || pathname.startsWith(`${basePath}/`));

  return hasBasePath ? pathname.slice(basePath.length) || "/" : pathname;
};

export const getInternalAppLink = (href?: string): InternalAppLink | null => {
  if (!href || href.startsWith("#")) return null;

  const baseUrlStr = getDefaultUrlBase();
  if (!URL.canParse(href, baseUrlStr)) return null;

  const baseUrl = new URL(baseUrlStr);
  const url = new URL(href, baseUrl);
  const pathname = stripBasePath(url.pathname);

  if (!isKnownAppRoutePathname(pathname)) return null;

  const displayPath = `${pathname}${url.search}${url.hash}`;
  assignToolboxSection(url.searchParams, AI_TOOLBOX_SECTION_VALUE);

  return {
    to: `${pathname}${url.search}${url.hash}`,
    displayPath,
  };
};

const getLinkText = (children: unknown) => {
  if (typeof children === "string") return children;
  return Array.isArray(children) &&
    children.every((child) => typeof child === "string")
    ? children.join("")
    : null;
};

const shouldReplaceLinkText = (text: string | null, href?: string) =>
  !!text && !!href && (text === href || text.startsWith("http"));

export const resolveMarkdownLinkText = ({
  children,
  href,
  displayPath,
}: {
  children: ReactNode;
  href?: string;
  displayPath: string;
}): ReactNode => {
  const linkText = getLinkText(children);
  return shouldReplaceLinkText(linkText, href) ? displayPath : children;
};
